<template>
  <div class="project-item" @click="goToAnimationDetail">
    <div class="status-indicators">
      <div v-if="!viewed" class="new-tag">{{ $t('projects.new') }}</div>
      <div v-if="isShowFavoriteStar" class="favorite-btn" @click.stop="toggleFavorite">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          :class="{ filled: isFavoriteLocal, outlined: !isFavoriteLocal }"
        >
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            stroke-width="1.5"
          />
        </svg>
      </div>
    </div>

    <div class="preview-wrapper">
      <img :src="finalImgUrl" :alt="imgName" class="preview-image" />
      <div v-if="isProcessing" class="processing-overlay">
        <div class="spinner">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
        <span>{{ $t('projects.processing') }}</span>
      </div>
      <div v-if="hasFailed" class="failed-overlay">
        <div class="error-icon">
          <span>!</span>
        </div>
        <span>{{ $t('projects.failed') }}</span>
      </div>
    </div>

    <div class="header-text caption" :title="imgName">
      {{ truncatedName }}
    </div>

    <div class="date-text caption">
      {{ relativeDate }}
    </div>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import { computed, ref, onMounted, watch } from "vue";
import defaultImage from "@/assets/project-placeholder.png";
import { useStore } from "vuex";
import { animationAPI } from "@/services/api";
import { useI18n } from 'vue-i18n';

const { locale, t } = useI18n();

const store = useStore();
const sessionId = store.getters.getSessionId;
const router = useRouter();

const emit = defineEmits(["processingStatusChange"]);

const props = defineProps({
  imgUrl: {
    type: String,
    default: defaultImage,
  },
  imgName: {
    type: String,
    required: false,
  },
  imgDate: {
    type: String,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  isShowFavoriteStar: {
    type: Boolean,
    default: true,
  },
  isFavorite: {
    type: Boolean,
    default: false,
  },
  viewed: {
    type: Boolean,
    default: true,
  },
  animationStatus: {
    type: String,
    default: "True",
  },
});

const finalImgUrl = computed(() => props.imgUrl || defaultImage);

const isFavoriteLocal = ref(props.isFavorite);

const isProcessing = computed(
  () => props.animationStatus === "Started" || props.animationStatus === "None"
);
const hasFailed = computed(() => props.animationStatus === "False");

watch(isProcessing, (newValue) => {
  emit("processingStatusChange", {
    id: props.id,
    isProcessing: newValue,
  });
});

onMounted(() => {
  isFavoriteLocal.value = props.isFavorite;
  if (isProcessing.value) {
    emit("processingStatusChange", {
      id: props.id,
      isProcessing: true,
    });
  }
});

const truncatedName = computed(() => {
  const maxLength = 20;
  if (!props.imgName) return "";
  return props.imgName.length > maxLength
    ? props.imgName.slice(0, maxLength) + "..."
    : props.imgName;
});

async function toggleFavorite() {
  try {
    const result = await animationAPI.toggleFavorite(sessionId, props.id);
    if (result.message) {
      isFavoriteLocal.value = !isFavoriteLocal.value;
    } else {
      console.error("Failed to update favorite status");
    }
  } catch (error) {
    console.error("Error setting favorite:", error);
  }
}

const relativeDate = computed(() => {
  if (props.imgDate === "None") return t('projects.noDate');

  const imgDate = new Date(props.imgDate + "Z");
  const now = new Date();

  const diffTime = (now - imgDate) / 1000;
  const formatter = new Intl.RelativeTimeFormat(locale.value, { numeric: "auto" });

  if (diffTime < 60) return formatter.format(-Math.round(diffTime), "seconds");
  if (diffTime < 3600) return formatter.format(-Math.round(diffTime / 60), "minutes");
  if (diffTime < 86400) return formatter.format(-Math.round(diffTime / 3600), "hours");
  return formatter.format(-Math.round(diffTime / 86400), "days");
});

const goToAnimationDetail = () => {
  router.push({ path: "/animation", query: { animationId: props.id } });
};
</script>

<style scoped>
.project-item {
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
}

.preview-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--color-white-10);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-sm-lg);
  margin-bottom: var(--spacing-sm);
  box-sizing: border-box;
  overflow: hidden;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: var(--border-radius-xs);
}

.header-text {
  width: 100%;
  height: 20px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: var(--font-size-md);
  line-height: 20px;
  padding: 0 var(--spacing-xs);
  box-sizing: border-box;
  margin-bottom: var(--spacing-xs);
}

.date-text {
  width: 100%;
  height: 16px;
  color: var(--color-white-20);
  text-align: left;
  font-size: var(--font-size-sm);
  line-height: 16px;
  padding: 0 var(--spacing-xs);
  box-sizing: border-box;
}

.status-indicators {
  position: absolute;
  top: var(--spacing-sm-lg);
  right: var(--spacing-sm-lg);
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  z-index: 2;
}

.favorite-btn {
  display: none;
  cursor: pointer;
}

.new-tag {
  background: var(--gradient-primary);
  color: var(--color-white);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-xs);
  font-size: var(--font-size-xs);
  font-weight: bold;
  box-shadow: var(--shadow-sm);
}

.project-item:hover .favorite-btn {
  display: block;
}

svg.filled path {
  fill: var(--color-gold);
  stroke: var(--color-gold);
}

svg.outlined path {
  fill: none;
  stroke: var(--color-white);
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--overlay-dark);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: var(--color-white);
  border-radius: var(--border-radius-lg);
  gap: var(--spacing-md);
}

.processing-overlay span {
  color: var(--color-white-70);
}

.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gradient-primary);
  animation: spin 1.5s infinite;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

.dot:nth-child(4) {
  animation-delay: 0.6s;
}

@keyframes spin {
  0%,
  100% {
    transform: scale(0.3);
    opacity: 0.3;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
}

.processing-spinner {
  display: none;
}

.failed-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--overlay-dark);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: var(--color-white);
  border-radius: var(--border-radius-lg);
  gap: var(--spacing-md);
}

.failed-overlay span {
  color: var(--color-white-70);
}

.error-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-error);
  font-size: var(--font-size-xl);
  font-weight: bold;
}

@media (max-width: 768px) {
  .header-text,
  .date-text {
    font-size: 0.85rem;
  }

  .status-indicators {
    top: 6px;
    right: 6px;
    gap: 6px;
  }

  .new-tag {
    font-size: 0.7rem;
    padding: 3px 6px;
  }
}
</style>
